import React, { useState, useMemo } from 'react';
import {
  Box,
  Card,
  Typography,
  IconButton,
  Stack,
  Chip,
  Tooltip,
  alpha,
  Button,
  Badge,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton as MuiIconButton,
  List,
  ListItem,
  ListItemText,
  Divider
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import TodayIcon from '@mui/icons-material/Today';
import CloseIcon from '@mui/icons-material/Close';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';

const PayablesCalendar = ({ payables = [], onViewDetails, canEdit = true, canDelete = true }) => {
  const theme = useTheme();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDayDialog, setSelectedDayDialog] = useState({ open: false, payables: [], date: null });

  // Get calendar data for current month
  const calendarData = useMemo(() => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    
    // Get first day of month and last day of month
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    
    // Get day of week for first day (0 = Sunday)
    const firstDayOfWeek = firstDay.getDay();
    
    // Calculate days to show from previous month
    const daysFromPrevMonth = firstDayOfWeek;
    
    // Calculate total days to show
    const totalDays = lastDay.getDate();
    const daysFromNextMonth = (7 - ((daysFromPrevMonth + totalDays) % 7)) % 7;
    
    // Build calendar array
    const calendar = [];
    
    // Previous month days
    const prevMonthLastDay = new Date(year, month, 0).getDate();
    for (let i = daysFromPrevMonth - 1; i >= 0; i--) {
      calendar.push({
        day: prevMonthLastDay - i,
        date: new Date(year, month - 1, prevMonthLastDay - i),
        isCurrentMonth: false,
        isToday: false
      });
    }
    
    // Current month days
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    for (let i = 1; i <= totalDays; i++) {
      const date = new Date(year, month, i);
      calendar.push({
        day: i,
        date: date,
        isCurrentMonth: true,
        isToday: date.getTime() === today.getTime()
      });
    }
    
    // Next month days
    for (let i = 1; i <= daysFromNextMonth; i++) {
      calendar.push({
        day: i,
        date: new Date(year, month + 1, i),
        isCurrentMonth: false,
        isToday: false
      });
    }
    
    return calendar;
  }, [currentDate]);

  // Group payables by date
  const payablesByDate = useMemo(() => {
    const grouped = {};
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    payables.forEach(payable => {
      if (!payable.invoice_due_date) return;
      
      const dueDate = new Date(payable.invoice_due_date);
      dueDate.setHours(0, 0, 0, 0);
      const dateKey = dueDate.toISOString().split('T')[0];
      
      if (!grouped[dateKey]) {
        grouped[dateKey] = [];
      }
      
      // Add urgency level
      const daysDiff = Math.floor((dueDate - today) / (1000 * 60 * 60 * 24));
      const urgency = payable.payment_status === 'paid' 
        ? 'paid' 
        : daysDiff < 0 
          ? 'overdue' 
          : daysDiff <= 7 
            ? 'urgent' 
            : daysDiff <= 30 
              ? 'soon' 
              : 'normal';
      
      grouped[dateKey].push({
        ...payable,
        urgency,
        daysDiff
      });
    });
    
    return grouped;
  }, [payables]);

  const handlePrevMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  };

  const handleNextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
  };

  const handleToday = () => {
    setCurrentDate(new Date());
  };

  const getUrgencyColor = (urgency) => {
    switch (urgency) {
      case 'paid':
        return {
          bg: alpha(theme.palette.success.main, 0.15),
          border: theme.palette.success.main,
          text: theme.palette.success.dark,
          badgeBg: theme.palette.success.main
        };
      case 'overdue':
        return {
          bg: alpha(theme.palette.error.main, 0.15),
          border: theme.palette.error.main,
          text: theme.palette.error.main,
          badgeBg: 'theme.palette.error.main'
        };
      case 'urgent':
        return {
          bg: alpha(theme.palette.warning.main, 0.15),
          border: theme.palette.warning.main,
          text: theme.palette.warning.dark,
          badgeBg: theme.palette.warning.main
        };
      case 'soon':
        return {
          bg: alpha(theme.palette.info.main, 0.15),
          border: theme.palette.info.main,
          text: theme.palette.info.dark,
          badgeBg: theme.palette.info.main
        };
      default:
        return {
          bg: alpha(theme.palette.grey[500], 0.08),
          border: theme.palette.grey[400],
          text: theme.palette.text.primary,
          badgeBg: theme.palette.grey[400]
        };
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  const monthYear = currentDate.toLocaleDateString('en-US', { 
    month: 'long', 
    year: 'numeric' 
  });

  const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  const handleCloseDayDialog = () => {
    setSelectedDayDialog({ open: false, payables: [], date: null });
  };

  return (
    <>
    <Card sx={{ 
      borderRadius: 3, 
      border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
      boxShadow: `0 2px 8px ${alpha(theme.palette.grey[900], 0.05)}`,
      overflow: 'hidden'
    }}>
      {/* Calendar Header */}
      <Box sx={{ 
        p: 3, 
        pb: 2,
        background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)}, ${alpha(theme.palette.primary.main, 0.02)})`,
        borderBottom: `1px solid ${alpha(theme.palette.grey[400], 0.5)}`
      }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="h5" sx={{ fontWeight: 500, color: 'text.primary' }}>
            {monthYear}
          </Typography>
          <Stack direction="row" spacing={1}>
            <Button
              size="small"
              startIcon={<TodayIcon />}
              onClick={handleToday}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 500,
                px: 2
              }}
            >
              Today
            </Button>
            <IconButton 
              onClick={handlePrevMonth}
              sx={{ 
                border: `1px solid ${alpha(theme.palette.grey[400], 0.5)}`,
                borderRadius: 2,
                '&:hover': {
                  background: alpha(theme.palette.primary.main, 0.08)
                }
              }}
            >
              <ChevronLeftIcon />
            </IconButton>
            <IconButton 
              onClick={handleNextMonth}
              sx={{ 
                border: `1px solid ${alpha(theme.palette.grey[400], 0.5)}`,
                borderRadius: 2,
                '&:hover': {
                  background: alpha(theme.palette.primary.main, 0.08)
                }
              }}
            >
              <ChevronRightIcon />
            </IconButton>
          </Stack>
        </Stack>

        {/* Legend */}
        <Stack direction="row" spacing={2} sx={{ mt: 2 }} flexWrap="wrap">
          <Stack direction="row" spacing={0.5} alignItems="center">
            <Box sx={{ 
              width: 12, 
              height: 12, 
              borderRadius: 1, 
              bgcolor: theme.palette.error.main 
            }} />
            <Typography variant="caption" color="text.secondary">Overdue</Typography>
          </Stack>
          <Stack direction="row" spacing={0.5} alignItems="center">
            <Box sx={{ 
              width: 12, 
              height: 12, 
              borderRadius: 1, 
              bgcolor: theme.palette.warning.main 
            }} />
            <Typography variant="caption" color="text.secondary">Due ≤7 days</Typography>
          </Stack>
          <Stack direction="row" spacing={0.5} alignItems="center">
            <Box sx={{ 
              width: 12, 
              height: 12, 
              borderRadius: 1, 
              bgcolor: theme.palette.info.main 
            }} />
            <Typography variant="caption" color="text.secondary">Due ≤30 days</Typography>
          </Stack>
          <Stack direction="row" spacing={0.5} alignItems="center">
            <Box sx={{ 
              width: 12, 
              height: 12, 
              borderRadius: 1, 
              bgcolor: theme.palette.success.main 
            }} />
            <Typography variant="caption" color="text.secondary">Paid</Typography>
          </Stack>
        </Stack>
      </Box>

      {/* Calendar Grid */}
      <Box sx={{ p: 2 }}>
        {/* Week day headers */}
        <Box sx={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(7, 1fr)', 
          gap: 1,
          mb: 1
        }}>
          {weekDays.map(day => (
            <Box 
              key={day}
              sx={{ 
                textAlign: 'center', 
                py: 1,
                borderRadius: 1,
                background: alpha(theme.palette.grey[500], 0.05)
              }}
            >
              <Typography 
                variant="caption" 
                sx={{ 
                  fontWeight: 500, 
                  color: 'text.secondary',
                  textTransform: 'uppercase',
                  letterSpacing: 0.5
                }}
              >
                {day}
              </Typography>
            </Box>
          ))}
        </Box>

        {/* Calendar days */}
        <Box sx={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(7, 1fr)', 
          gap: 1
        }}>
          {calendarData.map((dayData, index) => {
            const dateKey = dayData.date.toISOString().split('T')[0];
            const dayPayables = payablesByDate[dateKey] || [];
            const hasOverdue = dayPayables.some(p => p.urgency === 'overdue');
            const hasUrgent = dayPayables.some(p => p.urgency === 'urgent');
            const hasSoon = dayPayables.some(p => p.urgency === 'soon');
            const allPaid = dayPayables.length > 0 && dayPayables.every(p => p.urgency === 'paid');

            return (
              <Box
                key={index}
                sx={{
                  minHeight: 100,
                  p: 1,
                  border: `1px solid ${
                    dayData.isToday 
                      ? theme.palette.primary.main 
                      : alpha(theme.palette.grey[400], 0.3)
                  }`,
                  borderRadius: 2,
                  background: dayData.isToday 
                    ? alpha(theme.palette.primary.main, 0.05)
                    : dayData.isCurrentMonth 
                      ? 'white' 
                      : alpha(theme.palette.grey[500], 0.03),
                  opacity: (dayData.isCurrentMonth || hasOverdue) ? 1 : 0.5,
                  transition: 'all 0.2s ease',
                  cursor: dayPayables.length > 0 ? 'pointer' : 'default',
                  '&:hover': dayPayables.length > 0 ? {
                    boxShadow: `0 4px 12px ${alpha(theme.palette.grey[900], 0.1)}`,
                    transform: 'translateY(-2px)',
                    border: `1px solid ${theme.palette.primary.main}`
                  } : {}
                }}
              >
                <Stack spacing={0.5} sx={{ height: '100%' }}>
                  {/* Day number */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 500,
                        color: dayData.isToday 
                          ? theme.palette.primary.main 
                          : dayData.isCurrentMonth 
                            ? 'text.primary' 
                            : 'text.disabled',
                        width: 24,
                        height: 24,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderRadius: '50%',
                        background: dayData.isToday 
                          ? alpha(theme.palette.primary.main, 0.15) 
                          : 'transparent'
                      }}
                    >
                      {dayData.day}
                    </Typography>
                    {dayPayables.length > 0 && (
                      <Chip
                        label={dayPayables.length}
                        size="small"
                        sx={{
                          height: 20,
                          minWidth: 20,
                          fontSize: '0.7rem',
                          fontWeight: 500,
                          background: hasOverdue 
                            ? theme.palette.error.main
                            : hasUrgent 
                              ? theme.palette.warning.main 
                              : hasSoon 
                                ? theme.palette.info.main 
                                : allPaid
                                  ? theme.palette.success.main
                                  : theme.palette.grey[400],
                          color: 'white',
                          '& .MuiChip-label': {
                            px: 0.75,
                            fontWeight: 500
                          }
                        }}
                      />
                    )}
                  </Box>

                  {/* Payables for this day */}
                  <Stack spacing={0.5} sx={{ flex: 1, overflow: 'hidden' }}>
                    {dayPayables.slice(0, 3).map((payable, idx) => {
                      const colors = getUrgencyColor(payable.urgency);
                      return (
                        <Tooltip
                          key={idx}
                          title={
                            <Box>
                              <Typography variant="caption" sx={{ fontWeight: 500 }}>
                                {payable.supplier_name}
                              </Typography>
                              <Typography variant="caption" display="block">
                                {payable.item_name}
                              </Typography>
                              <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                                {formatCurrency(payable.remaining_amount)}
                              </Typography>
                              <Typography variant="caption" display="block" sx={{ color: 'white' }}>
                                {payable.urgency === 'overdue' && `${Math.abs(payable.daysDiff)} days overdue`}
                                {payable.urgency === 'urgent' && `Due in ${payable.daysDiff} days`}
                                {payable.urgency === 'soon' && `Due in ${payable.daysDiff} days`}
                                {payable.urgency === 'paid' && 'Paid'}
                              </Typography>
                            </Box>
                          }
                          arrow
                          placement="top"
                        >
                          <Box
                            onClick={() => onViewDetails && onViewDetails(payable)}
                            sx={{
                              p: 0.5,
                              px: 0.75,
                              borderRadius: 1,
                              background: colors.bg,
                              border: `1px solid ${colors.border}`,
                              cursor: 'pointer'
                            }}
                          >
                            <Typography
                              variant="caption"
                              sx={{
                                fontSize: '0.65rem',
                                fontWeight: 500,
                                color: colors.text,
                                display: 'block',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis'
                              }}
                            >
                              {payable.supplier_name}
                            </Typography>
                            <Typography
                              variant="caption"
                              sx={{
                                fontSize: '0.6rem',
                                fontWeight: 400,
                                color: colors.text,
                                opacity: 0.9,
                                display: 'block',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis'
                              }}
                            >
                              {formatCurrency(payable.remaining_amount)}
                            </Typography>
                          </Box>
                        </Tooltip>
                      );
                    })}
                    {dayPayables.length > 3 && (
                      <Box
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedDayDialog({
                            open: true,
                            payables: dayPayables,
                            date: dayData.date
                          });
                        }}
                        sx={{
                          p: 0.5,
                          borderRadius: 1,
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          '&:hover': {
                            background: alpha(theme.palette.primary.main, 0.1),
                            transform: 'scale(1.05)'
                          }
                        }}
                      >
                        <Typography
                          variant="caption"
                          sx={{
                            fontSize: '0.65rem',
                            color: theme.palette.primary.main,
                            textAlign: 'center',
                            fontWeight: 500,
                            display: 'block'
                          }}
                        >
                          +{dayPayables.length - 3} more
                        </Typography>
                      </Box>
                    )}
                  </Stack>
                </Stack>
              </Box>
            );
          })}
        </Box>
      </Box>
    </Card>

    {/* Day Details Dialog */}
    <Dialog
      open={selectedDayDialog.open}
      onClose={handleCloseDayDialog}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: `0 8px 32px ${alpha(theme.palette.grey[900], 0.15)}`
        }
      }}
    >
      <DialogTitle sx={{ 
        borderBottom: `1px solid ${alpha(theme.palette.grey[400], 0.5)}`,
        pb: 2
      }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 500 }}>
              Payables for {selectedDayDialog.date?.toLocaleDateString('en-US', { 
                month: 'long', 
                day: 'numeric',
                year: 'numeric'
              })}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {selectedDayDialog.payables.length} invoice{selectedDayDialog.payables.length !== 1 ? 's' : ''} due
            </Typography>
          </Box>
          <IconButton onClick={handleCloseDayDialog} size="small">
            <CloseIcon />
          </IconButton>
        </Stack>
      </DialogTitle>
      <DialogContent sx={{ p: 0 }}>
        <List sx={{ py: 0 }}>
          {selectedDayDialog.payables.map((payable, index) => {
            const colors = getUrgencyColor(payable.urgency);
            return (
              <React.Fragment key={index}>
                <ListItem
                  sx={{
                    py: 2,
                    px: 3,
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      background: alpha(theme.palette.primary.main, 0.05)
                    }
                  }}
                  onClick={() => {
                    handleCloseDayDialog();
                    onViewDetails && onViewDetails(payable);
                  }}
                >
                  <Box
                    sx={{
                      width: 4,
                      height: 40,
                      borderRadius: 1,
                      background: colors.border,
                      mr: 2
                    }}
                  />
                  <ListItemText
                    primary={
                      <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {payable.supplier_name}
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 500, color: colors.text }}>
                          {formatCurrency(payable.remaining_amount)}
                        </Typography>
                      </Stack>
                    }
                    secondary={
                      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 0.5 }}>
                        <Typography variant="body2" color="text.secondary">
                          {payable.item_name}
                        </Typography>
                        <Chip
                          label={
                            payable.urgency === 'overdue' 
                              ? `${Math.abs(payable.daysDiff)} days overdue`
                              : payable.urgency === 'paid'
                                ? 'Paid'
                                : `Due in ${payable.daysDiff} days`
                          }
                          size="small"
                          sx={{
                            height: 24,
                            fontSize: '0.7rem',
                            fontWeight: 500,
                            background: colors.bg,
                            color: colors.text,
                            border: `1px solid ${colors.border}`
                          }}
                        />
                      </Stack>
                    }
                  />
                </ListItem>
                {index < selectedDayDialog.payables.length - 1 && <Divider />}
              </React.Fragment>
            );
          })}
        </List>
      </DialogContent>
    </Dialog>
    </>
  );
};

export default PayablesCalendar;
